import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Card } from '@/components/ui/card';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { ChevronDown, ChevronUp, HelpCircle } from 'lucide-react';

const FAQPage = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      category: "Per Iniziare",
      questions: [
        {
          q: "Come posso registrarmi su ConsultantHUB?",
          a: "Per registrarti, clicca sul pulsante 'Accedi' nella home page e poi su 'Registrati'. Dovrai scegliere se sei un Professionista o un'Azienda, inserire i tuoi dati (nome, email, password) e confermare la registrazione. La registrazione è gratuita."
        },
        {
          q: "Qual è la differenza tra account Professionista e account Azienda?",
          a: "L'account Professionista è per consulenti, commercialisti, avvocati, ingegneri e altri professionisti che offrono servizi. L'account Azienda è per imprese che cercano professionisti. Ogni tipo di account ha funzionalità specifiche: i professionisti possono caricare CV e creare offerte di servizio, le aziende possono pubblicare richieste e visualizzare i profili professionali."
        },
        {
          q: "La registrazione è gratuita?",
          a: "Sì, la registrazione e l'utilizzo base della piattaforma sono completamente gratuiti. In futuro potrebbero essere introdotti piani premium con funzionalità aggiuntive."
        }
      ]
    },
    {
      category: "Profilo e Visibilità",
      questions: [
        {
          q: "Come completo il mio profilo professionale?",
          a: "Dopo la registrazione, vai nella sezione 'Profilo' dal menu. Qui puoi inserire: categoria professionale, settori di competenza, competenze specifiche, anni di esperienza, descrizione professionale, tariffa oraria, foto profilo, CV, contatti (email, telefono) e link ai tuoi profili social (LinkedIn, Facebook, Instagram)."
        },
        {
          q: "Posso caricare il mio CV?",
          a: "Sì, i professionisti possono caricare il proprio CV in formato PDF o DOCX. Il CV sarà visibile solo agli utenti registrati sulla piattaforma, garantendo privacy e professionalità."
        },
        {
          q: "Chi può vedere il mio profilo e i miei dati di contatto?",
          a: "Attualmente, tutti i dati del profilo (inclusi CV, contatti e social) sono visibili gratuitamente solo agli utenti registrati. In futuro potrebbe essere introdotto un sistema a pagamento per la visualizzazione di alcuni contenuti premium."
        },
        {
          q: "Come faccio a caricare il logo della mia azienda?",
          a: "Se hai un account Azienda, vai nella sezione 'Profilo' e troverai l'area per caricare il logo aziendale (JPG o PNG) e il company profile (PDF). Questi elementi rendono il tuo profilo più professionale e riconoscibile."
        }
      ]
    },
    {
      category: "Annunci",
      questions: [
        {
          q: "Come creo un annuncio?",
          a: "Dalla tua dashboard, clicca su 'Crea Annuncio' o 'Nuovo'. Dovrai specificare: tipo di annuncio (offerta o richiesta), titolo, categoria professionale, settore e descrizione dettagliata. I professionisti solitamente creano 'Offerte di Servizio', mentre le aziende creano 'Richieste di Servizio'."
        },
        {
          q: "Posso modificare un annuncio già pubblicato?",
          a: "Sì, puoi modificare i tuoi annunci. Vai al dettaglio dell'annuncio e clicca su 'Modifica Annuncio'. Potrai cambiare titolo, descrizione, settore e tipo di annuncio. NOTA IMPORTANTE: la categoria professionale NON può essere modificata per evitare riutilizzo dello stesso annuncio per diverse figure professionali. Per cambiare categoria, devi creare un nuovo annuncio."
        },
        {
          q: "Come cerco professionisti o offerte di lavoro?",
          a: "Vai nella sezione 'Annunci' e usa i filtri disponibili: puoi filtrare per categoria professionale, settore, tipo di annuncio (offerta/richiesta) e utilizzare la barra di ricerca. Puoi anche attivare il filtro 'Nascondi annunci già contattati' per vedere solo nuove opportunità."
        },
        {
          q: "Posso eliminare un annuncio?",
          a: "Sì, puoi eliminare i tuoi annunci in qualsiasi momento dalla sezione di gestione annunci nella tua dashboard."
        }
      ]
    },
    {
      category: "Messaggistica e Contatti",
      questions: [
        {
          q: "Come contatto un professionista o un'azienda?",
          a: "Clicca sull'annuncio di tuo interesse e usa il pulsante 'Contatta'. Si aprirà una finestra dove potrai scrivere il tuo messaggio. Il destinatario riceverà la tua richiesta e potrà risponderti tramite il sistema di messaggistica interna."
        },
        {
          q: "Posso vedere il profilo completo di chi mi scrive?",
          a: "Sì, dalla sezione Messaggi, clicca sul pulsante 'Visualizza Profilo' nell'intestazione della conversazione. Potrai vedere il profilo completo con competenze, esperienza, contatti e link social del professionista o dell'azienda."
        },
        {
          q: "Come gestisco le conversazioni?",
          a: "Tutte le tue conversazioni sono disponibili nella sezione 'Messaggi'. Puoi vedere l'elenco delle conversazioni attive, il numero di messaggi non letti e accedere alle chat per rispondere."
        },
        {
          q: "I miei dati di contatto (email, telefono) sono sicuri?",
          a: "I tuoi dati di contatto sono visibili solo agli utenti registrati sulla piattaforma. Puoi scegliere quali informazioni condividere nel tuo profilo."
        }
      ]
    },
    {
      category: "Categorie e Settori",
      questions: [
        {
          q: "Quali categorie professionali sono disponibili?",
          a: "ConsultantHUB include le seguenti categorie: Architetti, Avvocati, Commercialisti, Consulenti Aziendali, Consulenti del Lavoro, Consulenti Finanziari, Consulenti Fiscali, Consulenti HR, Consulenti IT, Consulenti Legali Aziendali, Consulenti Marketing, Esperti Sicurezza sul Lavoro, Ingegneri, Medici del Lavoro, Notai. Le categorie vengono ampliate in base alle richieste degli utenti."
        },
        {
          q: "In quali settori posso operare?",
          a: "I settori disponibili sono: Agenzia Viaggio, Commercio, Energia, Finanza, GDO, Immobiliare, Industria, Lavoratore Autonomo, Manifatturiero, Ricettivo, Sanità, Servizi, Retail, Tecnologia, Trasporti e Logistica, e Altro. Puoi selezionare più settori nel tuo profilo."
        },
        {
          q: "Posso richiedere l'aggiunta di una nuova categoria professionale?",
          a: "Sì! Le categorie professionali vengono implementate step by step in base alle richieste degli utenti. Contattaci tramite la sezione 'Contatti' per proporre nuove categorie."
        }
      ]
    },
    {
      category: "Account e Sicurezza",
      questions: [
        {
          q: "Come posso modificare la mia password?",
          a: "Attualmente puoi modificare la password contattando il supporto. In futuro sarà disponibile una funzione di cambio password direttamente dal profilo."
        },
        {
          q: "Cosa succede se dimentico la password?",
          a: "Contatta il supporto tramite la sezione 'Contatti' fornendo l'email di registrazione. Ti aiuteremo a recuperare l'accesso al tuo account."
        },
        {
          q: "Posso eliminare il mio account?",
          a: "Sì, puoi richiedere l'eliminazione del tuo account contattando il supporto. Tutti i tuoi dati verranno rimossi dal sistema entro 30 giorni come previsto dal GDPR."
        },
        {
          q: "Come gestisco le notifiche email?",
          a: "Vai nella sezione 'Impostazioni' dal menu del tuo profilo. Qui puoi attivare o disattivare le notifiche per nuovi messaggi, recensioni e comunicazioni promozionali."
        }
      ]
    },
    {
      category: "Verifica Profilo",
      questions: [
        {
          q: "Cos'è il badge 'Profilo Verificato'?",
          a: "Il badge verificato indica che il professionista ha superato un processo di verifica della propria identità e credenziali professionali. Questo aumenta la fiducia da parte delle aziende."
        },
        {
          q: "Come posso richiedere la verifica del mio profilo?",
          a: "Dalla pagina del tuo profilo, clicca su 'Richiedi Verifica'. Sarà necessario fornire documentazione come documento d'identità e certificato di iscrizione all'albo professionale (se applicabile). Il nostro team esaminerà la richiesta."
        },
        {
          q: "Quanto tempo richiede la verifica?",
          a: "La verifica viene generalmente completata entro 5-7 giorni lavorativi. Riceverai una notifica email sull'esito della verifica."
        },
        {
          q: "Cosa succede se la verifica viene rifiutata?",
          a: "Se la verifica viene rifiutata, riceverai una email con le motivazioni. Potrai correggere eventuali errori e ripresentare la richiesta."
        }
      ]
    },
    {
      category: "Recensioni",
      questions: [
        {
          q: "Chi può lasciare una recensione?",
          a: "Solo le aziende che hanno avuto una conversazione con un professionista possono lasciare una recensione. Questo garantisce che le recensioni siano basate su interazioni reali."
        },
        {
          q: "Come funziona il sistema di recensioni?",
          a: "Le aziende possono valutare i professionisti da 1 a 5 stelle, specificando il tipo di collaborazione e confermando che c'è stato un rapporto di consulenza/collaborazione. Le recensioni sono pubbliche e visibili sul profilo del professionista."
        },
        {
          q: "Posso rispondere a una recensione?",
          a: "Attualmente non è possibile rispondere pubblicamente alle recensioni. Se ritieni che una recensione sia inappropriata o falsa, puoi segnalarla al supporto."
        },
        {
          q: "Come segnalo una recensione inappropriata?",
          a: "Vai alla sezione 'Segnala un Problema' e seleziona 'Recensione inappropriata'. Fornisci i dettagli e il nostro team esaminerà la segnalazione."
        }
      ]
    },
    {
      category: "Segnalazioni e Reclami",
      questions: [
        {
          q: "Come segnalo un comportamento scorretto?",
          a: "Utilizza la sezione 'Segnala un Problema' accessibile dal footer o dal menu. Seleziona la categoria appropriata (comportamento scorretto, frode, recensione inappropriata) e fornisci tutti i dettagli."
        },
        {
          q: "Cosa succede dopo aver inviato una segnalazione?",
          a: "Riceverai una conferma email con il numero della segnalazione. Il nostro team esaminerà il caso e ti informerà sull'esito. I tempi variano in base alla complessità del caso."
        },
        {
          q: "Le mie segnalazioni sono anonime?",
          a: "Le segnalazioni non sono anonime per il nostro team, ma la tua identità non viene rivelata all'utente segnalato durante l'indagine, salvo casi specifici previsti dalla legge."
        },
        {
          q: "Quali provvedimenti possono essere presi?",
          a: "In base alla gravità della violazione, i provvedimenti possono includere: avvertimento, sospensione temporanea, rimozione di contenuti o cancellazione permanente dell'account."
        }
      ]
    },
    {
      category: "Piani e Costi",
      questions: [
        {
          q: "Quanto costa utilizzare ConsultantHUB?",
          a: "Attualmente l'utilizzo di ConsultantHUB è completamente gratuito. Registrazione, creazione profilo, pubblicazione annunci, messaggistica e visualizzazione profili sono tutti gratuiti."
        },
        {
          q: "Ci saranno piani a pagamento in futuro?",
          a: "Stiamo valutando l'introduzione di piani premium con funzionalità avanzate come: maggiore visibilità degli annunci, badge verificato, statistiche avanzate e supporto prioritario. Tutti gli utenti registrati saranno informati in anticipo."
        },
        {
          q: "Come posso ottenere maggiore visibilità per i miei annunci?",
          a: "Per ora, assicurati di completare il tuo profilo al 100%, pubblicare annunci dettagliati e rispondere velocemente ai messaggi. In futuro potrebbero essere disponibili opzioni a pagamento per aumentare la visibilità."
        }
      ]
    }
  ];

  const toggleQuestion = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  let questionIndex = 0;

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      
      <div className="max-w-4xl mx-auto px-6 py-8 mt-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Header */}
          <div className="text-center mb-12">
            <div className="w-16 h-16 bg-gradient-to-br from-[#344B71] to-[#344B71] rounded-full flex items-center justify-center mx-auto mb-4">
              <HelpCircle className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl font-playfair font-bold text-[#344B71] mb-4" data-testid="faq-title">
              Domande Frequenti (FAQ)
            </h1>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Trova risposte alle domande più comuni su ConsultantHUB. Se non trovi quello che cerchi, contattaci!
            </p>
          </div>

          {/* FAQ Categories */}
          <div className="space-y-8">
            {faqs.map((category, catIndex) => (
              <div key={catIndex}>
                <h2 className="text-2xl font-playfair font-bold text-[#344B71] mb-4 pb-2 border-b-2 border-[#E8A007]">
                  {category.category}
                </h2>
                <div className="space-y-3">
                  {category.questions.map((item) => {
                    const currentIndex = questionIndex++;
                    const isOpen = openIndex === currentIndex;
                    
                    return (
                      <Card key={currentIndex} className="bg-white overflow-hidden">
                        <button
                          onClick={() => toggleQuestion(currentIndex)}
                          className="w-full p-6 text-left flex items-center justify-between hover:bg-slate-50 transition-colors"
                          data-testid={`faq-question-${currentIndex}`}
                        >
                          <h3 className="text-lg font-semibold text-[#344B71] pr-4">
                            {item.q}
                          </h3>
                          {isOpen ? (
                            <ChevronUp className="w-5 h-5 text-[#344B71] flex-shrink-0" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-slate-600 flex-shrink-0" />
                          )}
                        </button>
                        {isOpen && (
                          <motion.div
                            initial={{ height: 0 }}
                            animate={{ height: 'auto' }}
                            exit={{ height: 0 }}
                            className="px-6 pb-6"
                          >
                            <p className="text-slate-700 leading-relaxed" data-testid={`faq-answer-${currentIndex}`}>
                              {item.a}
                            </p>
                          </motion.div>
                        )}
                      </Card>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* CTA */}
          <Card className="mt-12 p-8 bg-gradient-to-br from-[#344B71] to-[#344B71] text-white text-center">
            <h3 className="text-2xl font-playfair font-bold mb-3">Non hai trovato la risposta?</h3>
            <p className="text-slate-200 mb-6">
              Il nostro team di supporto è qui per aiutarti. Contattaci e ti risponderemo il prima possibile.
            </p>
            <button
              onClick={() => window.location.href = '/contatti'}
              className="bg-[#E8A007] hover:bg-[#E09612] text-white px-8 py-3 rounded-full font-medium transition-all shadow-lg hover:shadow-xl"
            >
              Contattaci
            </button>
          </Card>
        </motion.div>
      </div>
      <Footer />
    </div>
  );
};

export default FAQPage;
